import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Helmet } from "react-helmet-async";
import { Search, ArrowRight, Lock } from "lucide-react";
import TradesPurpleLayout from "../components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import LoggedInLandingPage from "./LoggedInLandingPage";
import {
  validatePostcode,
  coarsenPostcode,
  normalizePostcode,
  detectCountry,
} from "@/lib/postcodeUtils";

export default function LandingPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [postcode, setPostcode] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Process the postcode and navigate if valid
  const processPostcode = useCallback(
    (postcodeValue: string) => {
      // Normalize the postcode input
      const normalizedInput = normalizePostcode(postcodeValue);

      if (!normalizedInput) {
        setError("Please enter your postcode");
        return false;
      }

      // Validate the postcode for any supported country
      if (!validatePostcode(normalizedInput)) {
        const country = detectCountry(normalizedInput);
        if (country) {
          // Country detected but validation failed - specific error
          setError(
            `Please enter a valid ${country === "GB" ? "UK" : country} postcode`,
          );
        } else {
          // No country detected - generic error
          setError("Please enter a valid post/ZIP code");
        }
        return false;
      }

      // Coarsen the postcode for privacy (extract area-level only)
      const coarsenedPostcode = coarsenPostcode(normalizedInput);

      // Clear any previous errors
      setError(null);

      // Navigate directly to the postcode route
      navigate(`/${coarsenedPostcode}`);

      return true;
    },
    [navigate, setError],
  );

  // Handle form submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    processPostcode(postcode);
  };

  // All useEffect hooks must be declared at top level, not after conditionals

  // Removed auto-navigation effect - now requires users to press Enter

  // Early return for authenticated users
  if (isAuthenticated) {
    return <LoggedInLandingPage />;
  }

  return (
    <TradesPurpleLayout>
      <Helmet>
        <title>TradesPurple | Find the tradespeople your neighbours trust</title>
        <meta
          name="description"
          content="Find the tradespeople your neighbours trust. Word of mouth: made digital."
        />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
        <meta name="theme-color" content="#7e22ce" />
      </Helmet>

      {/* Main content wrapper */}
      <div className="flex flex-col px-4">
        {/* Centered content */}
        <div className="flex flex-col items-center">
          {/* Search card - centered on desktop */}
          <Card className="w-full md:w-4/5 max-w-lg border-2 border-purple-900 rounded-xl shadow-lg">
            <CardContent className="p-5 md:p-6">
              <form onSubmit={handleSearch}>
                <div className="space-y-2">
                  <div>
                    <div className="flex space-x-2">
                      <div className="relative flex-grow">
                        <Input
                          id="postcode"
                          placeholder="Enter post/ZIP code"
                          className="border-2 border-purple-900 pl-10 py-5 text-lg placeholder:text-purple-500"
                          value={postcode}
                          onChange={(e) => {
                            // Allow international postcodes and normalize case
                            const value = e.target.value.toUpperCase();
                            setPostcode(value);
                          }}
                          aria-label="Enter your postcode or ZIP code"
                          autoComplete="postal-code"
                          type="text"
                          name="postcode"
                        />
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                          <Search className="h-5 w-5 text-purple-700" />
                        </div>

                        {/* Lock icon with tooltip - properly positioned */}
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center group">
                          <Lock className="h-4 w-4 text-purple-700 opacity-70" />
                          <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-200 right-0 -top-16 bg-white border border-purple-200 rounded-md shadow-md p-2 w-60 text-xs text-gray-700 pointer-events-none">
                            Your postal code is only used to find local
                            tradespeople. We don't store or share your details.
                          </div>
                        </div>
                      </div>
                      <Button
                        type="submit"
                        className="flex items-center justify-center py-5 bg-purple-700 hover:bg-purple-800 text-white font-medium px-5"
                      >
                        <ArrowRight className="h-5 w-5" />
                      </Button>
                    </div>
                    {error && (
                      <p className="mt-2 text-red-600 text-sm">{error}</p>
                    )}

                    {/* New section - OR divider with CTA button */}
                    <div className="mt-5 text-center">
                      <div className="relative flex items-center justify-center">
                        <div className="border-t border-purple-200 absolute w-full"></div>
                        <span className="bg-white px-2 text-sm text-purple-500 relative">
                          OR
                        </span>
                      </div>
                      <Link
                        to="/auth/signup"
                        className="mt-4 inline-flex items-center justify-center px-5 py-3 bg-white hover:bg-purple-50 text-purple-700 font-medium rounded-lg transition shadow-sm border-2 border-purple-700 w-full"
                      >
                        Add my first tradesperson
                      </Link>
                    </div>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Benefit pills - moved below search box */}
          <div className="flex flex-wrap justify-center gap-2 mt-4 mb-3">
            <span className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
              <span className="mr-1">✓</span> Free
            </span>
            <span className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
              <span className="mr-1">✓</span> Local
            </span>
            <span className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
              <span className="mr-1">✓</span> Fast
            </span>
          </div>

          {/* Supported countries indicator - below benefit pills */}
          <div className="flex items-center justify-center group mb-6">
            <div className="flex items-center space-x-1 opacity-60 hover:opacity-100 transition-opacity cursor-help">
              <span className="text-sm">🇬🇧🇮🇪🇺🇸🇨🇦🇦🇺🇳🇱🇲🇹</span>
            </div>
            {/* Tooltip */}
            <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-200 bottom-full mb-2 bg-white border border-purple-200 rounded-md shadow-lg p-3 text-xs text-gray-700 pointer-events-none z-10">
              <div className="text-center">
                <div className="font-medium text-purple-700 mb-1">
                  Supported Countries
                </div>
                <div className="space-y-1 text-left">
                  <div>🇬🇧 United Kingdom (HP22, SW1A 1AA)</div>
                  <div>🇮🇪 Ireland (D02 XY45)</div>
                  <div>🇺🇸 United States (90210)</div>
                  <div>🇨🇦 Canada (M4C 1B5)</div>
                  <div>🇦🇺 Australia (2000)</div>
                  <div>🇳🇱 Netherlands (1013 AB)</div>
                  <div>🇲🇹 Malta (VLT 1234)</div>
                </div>
              </div>
            </div>
          </div>

          {/* Small spacer */}
          <div className="h-4 md:h-8"></div>
        </div>
      </div>

      {/* How It Works section */}
      <div id="benefits-section" className="mt-4 md:mt-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="relative inline-block mb-6">
              <div className="bg-white border-2 border-purple-300 rounded-2xl px-6 py-4 shadow-lg relative">
                <p className="text-lg md:text-xl font-semibold text-purple-900 m-0">
                  "Can anyone recommend a plumber?"
                </p>
                {/* Speech bubble tail */}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[15px] border-r-[15px] border-t-[15px] border-l-transparent border-r-transparent border-t-purple-300"></div>
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 translate-y-[-2px] w-0 h-0 border-l-[13px] border-r-[13px] border-t-[13px] border-l-transparent border-r-transparent border-t-white"></div>
              </div>
            </div>
            <p className="text-center text-purple-700 text-lg font-medium">
              TradesPurple is your community's<br />
              little black book.
            </p>
          </div>

          {/* Responsive grid for desktop and mobile */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <div className="text-center">
              <div className="rounded-full bg-purple-100 p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-purple-700"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-purple-900 mb-2">
                True word of mouth
              </h3>
              <p className="text-purple-700 text-base">
                No more searching your contacts or scraps of paper for "Gary Chimney man". Share your link — neighbours see every tradesperson you trust. And vice versa.
              </p>
            </div>

            <div className="text-center">
              <div className="rounded-full bg-purple-100 p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-purple-700"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-purple-900 mb-2">
                One place to go
              </h3>
              <p className="text-purple-700 text-base">
                Always know where to look — your shared directory lives on your phone, and it's the quickest way to make contact. We'll write a first draft, and you can send it to one or many at once.
              </p>
            </div>
          </div>
        </div>

        {/* Clean CTA button */}
        <div className="mt-8 text-center">
          <Link
            to="/auth/signup"
            className="inline-block px-8 py-4 bg-purple-700 hover:bg-purple-800 text-white font-semibold text-lg rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
          >
            Sign up for free
          </Link>
        </div>
      </div>
    </TradesPurpleLayout>
  );
}
